import { IconographyTypes } from '@genial/icons';
import { BackendError, Service } from '@genial/services';
import { Utils } from '@genial/utils';
import { selector, selectorFamily } from 'recoil';

import { blankMetaResponse } from '../../../../utils/blankMetaResponse';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../../master/selector';
import { screenName } from '../../constants';
import { movementOptionQueryState, movementsFilterParamsState } from '../atom';
import {
  FinancialMovementsResponseNormalized,
  MovementOptionQuery,
  MovementsFilterParamsProps,
} from '../types';

export const updateMovementOptionSelector = selector({
  key: 'updateMovementOptionSelector',
  set: ({ set }, value) => {
    set(movementOptionQueryState, value as MovementOptionQuery);
  },
  get: ({ get }) => {
    const params = get(movementOptionQueryState);
    return params;
  },
});

export const updateMovementFilterParamsSelector = selector({
  key: 'updateMovementFilterParamsSelector',
  set: ({ set, get }, value) => {
    const currentParams = get(movementsFilterParamsState);
    const data = value as MovementsFilterParamsProps;

    const params = { ...currentParams, ...data } as MovementsFilterParamsProps;
    set(movementsFilterParamsState, params);
  },
  get: ({ get }) => {
    const params = get(movementsFilterParamsState);
    return params;
  },
});

export const movementStatementsQuery = selectorFamily<
  { data: FinancialMovementsResponseNormalized[] } | null | undefined,
  {
    accountNumber: number;
  }
>({
  key: 'movementStatementsQuery',
  get: ({ accountNumber }) => {
    return async ({ get }) => {
      try {
        const productType = get(movementOptionQueryState);
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);
        const movementsFilterParams = get(movementsFilterParamsState);

        const { data } = await Service.Finance.V1.getMovementByAccountNumber({
          ...movementsFilterParams,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
          accountNumber,
          screenName,
          productType,
        });

        const normalizedData: FinancialMovementsResponseNormalized[] =
          data?.map((item) => ({
            ...item,
            statusIcon: {
              ...item.statusIcon,
              iconNormalized: (item.statusIcon?.icon || 'Info') as IconographyTypes.Option,
              iconColorNormalized: Utils.Color.resolve(item.statusIcon?.iconColor),
              backgroundColorNormalized: Utils.Color.resolve(item.statusIcon.backgroundColor),
              iconSizeNormalized: (item.statusIcon?.iconSize || 'xs') as Utils.IconTypes.SizeTokens,
            },
          })) || [];

        return { data: normalizedData };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});
